import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useUserContext } from './UserContext';
import {
    AccountStatus,
    ModuleKey,
    ModuleSubscription,
    Invoice,
    MonthlySummary,
} from '../types/subscriptionTypes';
import {
    getTrialDaysRemaining,
    isTrialExpired,
    buildMonthlySummary,
    isModuleAccessible,
} from '../utils/subscriptionUtils';
import {
    CORE_PROGRAM_DEFAULT_RATE,
} from '../config/subscriptionConfig';
import { fetchPMConfigurationsByCompany } from '../graphql/pmQueries';
import { countPmFormsForBilling } from '../utils/pmBillingUtils';

const client = generateClient({ authMode: 'userPool' });

// ─── Context Type ───

interface SubscriptionContextValue {
    // State
    accountStatus: AccountStatus | null;
    trialDaysRemaining: number | null;
    isTrialActive: boolean;
    isCoreActive: boolean;
    moduleSubscriptions: ModuleSubscription[];
    invoices: Invoice[];
    loading: boolean;

    // Derived helpers
    isModuleActive: (moduleKey: ModuleKey) => boolean;
    isModulePendingCancel: (moduleKey: ModuleKey) => boolean;
    isModuleLocked: (moduleKey: ModuleKey) => boolean;
    getModuleSubscription: (moduleKey: ModuleKey) => ModuleSubscription | null;
    monthlySummary: MonthlySummary;

    // Refresh
    refreshSubscription: () => Promise<void>;
}

const defaultSummary: MonthlySummary = { coreProgram: 0, addOns: [], total: 0 };

const SubscriptionContext = createContext<SubscriptionContextValue>({
    accountStatus: null,
    trialDaysRemaining: null,
    isTrialActive: false,
    isCoreActive: false,
    moduleSubscriptions: [],
    invoices: [],
    loading: false,
    isModuleActive: () => false,
    isModulePendingCancel: () => false,
    isModuleLocked: () => true,
    getModuleSubscription: () => null,
    monthlySummary: defaultSummary,
    refreshSubscription: async () => { },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useSubscriptionContext = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
    const { company } = useUserContext();
    const [moduleSubscriptions, setModuleSubscriptions] = useState<ModuleSubscription[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(false);
    const [pmFormCount, setPmFormCount] = useState(0);

    const companyId = company?.id;

    // ─── Derived Account State ───

    const accountStatus = (company as Record<string, unknown>)?.accountStatus as AccountStatus | null ?? null;
    const trialEndDate = (company as Record<string, unknown>)?.trialEndDate as string | null ?? null;
    const addOnGracePeriodEndDate = (company as Record<string, unknown>)?.addOnGracePeriodEndDate as string | null ?? null;
    const coreMonthlyRate = ((company as Record<string, unknown>)?.coreMonthlyRate as number) || CORE_PROGRAM_DEFAULT_RATE;

    const trialDaysRemaining = useMemo(() => {
        if (accountStatus !== 'trial') return null;
        return getTrialDaysRemaining(trialEndDate);
    }, [accountStatus, trialEndDate]);

    const isTrialActive = accountStatus === 'trial' && !isTrialExpired(trialEndDate);
    const isCoreActive = accountStatus === 'active' || accountStatus === 'past-due';

    // ─── Fetch Module Subscriptions & Invoices ───

    const refreshSubscription = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            // Fetch module subscriptions
            const subResult = await client.graphql({
                query: `query ListModuleSubscriptions($companyId: String!) {
                    listModuleSubscriptionByCompanyId(companyId: $companyId) {
                        items { id companyId moduleKey status activatedAt cancelRequestedAt effectiveEndDate pricingModel monthlyRate }
                    }
                }`,
                variables: { companyId },
            });
            const subs = (subResult as any).data?.listModuleSubscriptionByCompanyId?.items || [];
            setModuleSubscriptions(subs as ModuleSubscription[]);

            // Fetch invoices
            const invResult = await client.graphql({
                query: `query ListInvoices($companyId: String!) {
                    listInvoiceByCompanyIdAndDate(companyId: $companyId, sortDirection: DESC) {
                        items { id companyId invoiceNumber date description amount status pdfUrl lineItems }
                    }
                }`,
                variables: { companyId },
            });
            const invs = (invResult as any).data?.listInvoiceByCompanyIdAndDate?.items || [];
            setInvoices(invs as Invoice[]);

            try {
                const pmConfigs = await fetchPMConfigurationsByCompany(companyId);
                setPmFormCount(countPmFormsForBilling(pmConfigs || []));
            } catch (pmError) {
                console.error('Error fetching PM form count for billing:', pmError);
                setPmFormCount(0);
            }
        } catch (error) {
            console.error('Error fetching subscription data:', error);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (companyId) refreshSubscription();
        else setPmFormCount(0);
    }, [companyId, refreshSubscription]);

    // ─── Module Helpers ───

    const getModuleSubscription = useCallback(
        (moduleKey: ModuleKey): ModuleSubscription | null => {
            return moduleSubscriptions.find(
                (s) => s.moduleKey === moduleKey && s.status !== 'cancelled'
            ) || null;
        },
        [moduleSubscriptions]
    );

    const isModuleActive = useCallback(
        (moduleKey: ModuleKey): boolean => {
            const sub = getModuleSubscription(moduleKey);
            return isModuleAccessible(accountStatus, sub, addOnGracePeriodEndDate);
        },
        [accountStatus, addOnGracePeriodEndDate, getModuleSubscription]
    );

    const isModulePendingCancel = useCallback(
        (moduleKey: ModuleKey): boolean => {
            const sub = getModuleSubscription(moduleKey);
            return sub?.status === 'pending-cancel';
        },
        [getModuleSubscription]
    );

    const isModuleLocked = useCallback(
        (moduleKey: ModuleKey): boolean => {
            return !isModuleActive(moduleKey);
        },
        [isModuleActive]
    );

    // ─── Monthly Summary ───
    // Tires still stubbed until equipment enrollment is wired here.
    const enrolledCounts: Record<string, number> = useMemo(() => ({
        pm: pmFormCount,
        tires: 0,
    }), [pmFormCount]);

    const monthlySummary = useMemo(() => {
        if (!isCoreActive) return defaultSummary;
        return buildMonthlySummary(coreMonthlyRate, moduleSubscriptions, enrolledCounts);
    }, [isCoreActive, coreMonthlyRate, moduleSubscriptions, enrolledCounts]);

    // ─── Context Value ───

    const value: SubscriptionContextValue = {
        accountStatus,
        trialDaysRemaining,
        isTrialActive,
        isCoreActive,
        moduleSubscriptions,
        invoices,
        loading,
        isModuleActive,
        isModulePendingCancel,
        isModuleLocked,
        getModuleSubscription,
        monthlySummary,
        refreshSubscription,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
